import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackMeta } from "@/lib/meta";
import { getAttribution } from "@/lib/attribution";
import { CheckCircle2, Loader2 } from "lucide-react";

const digits = (v) => (v || "").replace(/\D/g, "");
const phoneOk = (v) => digits(v).length >= 10;
const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const COPY = {
  demo: { title: "Book a Demo", desc: "See RecoverOS in action. Tell us a bit about your business and we'll reach out." },
  trial: { title: "Start with Intro PAYG", desc: "₹0 for 3 months — up to 100 accounts, 1 user and up to 250 email communications with your included Free RC. No subscription required." },
  partner: { title: "Become a Partner", desc: "Earn referral incentives and revenue share. Tell us about yourself." },
  contact: { title: "Contact Us", desc: "Questions about RecoverOS? Send us a note and we'll get back to you." },
};

export const LeadDialog = ({ open, onOpenChange, kind = "demo", plan = "", prefill = null }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", whatsapp: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const meta = COPY[kind] || COPY.demo;
  const phoneRequired = kind === "demo" || kind === "partner";
  const showPreview = kind === "demo" || kind === "trial";

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm({
        name: prefill?.name || "", email: prefill?.email || "", phone: "",
        company: prefill?.company || "", message: "", whatsapp: false,
      });
    }
  }, [open, kind, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());
    if (!form.name.trim() || !emailOk) {
      toast.error("Please enter your name and a valid email");
      return;
    }
    if (phoneRequired && !phoneOk(form.phone)) {
      toast.error("Please enter a valid phone number (min 10 digits)");
      return;
    }
    if (form.phone.trim() && !phoneOk(form.phone)) {
      toast.error("Please enter a valid phone number (min 10 digits)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        message: form.message, whatsapp_opt_in: form.whatsapp, kind, plan, attribution: getAttribution(),
      });
      trackMeta("Lead", {
        customData: { lead_type: kind, plan: plan || "" },
        email: form.email,
        phone: form.phone,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${showPreview && !done ? "sm:max-w-3xl" : "sm:max-w-md"} rounded-none border-2 border-slate-200 p-0 gap-0 overflow-hidden`} data-testid="lead-dialog">
        <div className={showPreview && !done ? "grid sm:grid-cols-[1.05fr_0.95fr]" : ""}>
          <div className="p-6">
        {done ? (
          <div className="py-8 text-center" data-testid="lead-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#16A34A]" />
            <h3 className="mt-4 text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Thank you, {form.name.split(" ")[0]}!</h3>
            <p className="mt-2 text-sm text-slate-600">Our team will contact you at <strong>{form.email}</strong> shortly.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{meta.title}</DialogTitle>
              <DialogDescription className="text-slate-600">{meta.desc}{plan ? ` — ${plan} plan` : ""}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Full Name *</Label>
                <Input value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-none mt-1" data-testid="lead-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Email *</Label>
                  <Input value={form.email} onChange={set("email")} placeholder="you@company.com" className="rounded-none mt-1" data-testid="lead-email" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Phone {phoneRequired ? "*" : <span className="font-normal text-slate-400">(optional)</span>}</Label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" className="rounded-none mt-1" data-testid="lead-phone" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="lead-whatsapp-label">
                <input type="checkbox" checked={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.checked }))} className="h-4 w-4 accent-[#16A34A]" data-testid="lead-whatsapp" />
                <span className="text-xs text-slate-600">I prefer to be contacted on WhatsApp</span>
              </label>
              <div>
                <Label className="text-xs font-semibold text-slate-700">{kind === "partner" ? "Firm / Practice" : "Company"}</Label>
                <Input value={form.company} onChange={set("company")} placeholder="Organization name" className="rounded-none mt-1" data-testid="lead-company" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Message</Label>
                <Textarea value={form.message} onChange={set("message")} placeholder="Tell us about your needs…" className="rounded-none mt-1" rows={3} data-testid="lead-message" />
              </div>
              <button
                onClick={submit}
                disabled={busy}
                className="w-full bg-[#0B3D91] text-white rounded-none px-6 py-3 font-semibold hover:bg-[#0a347d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="lead-submit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? "Submitting…" : meta.title}
              </button>
            </div>
          </>
        )}
          </div>
          {showPreview && !done && (
            <div className="relative hidden overflow-hidden bg-[#070c1a] p-7 text-white sm:flex sm:flex-col sm:justify-center" data-testid="lead-preview-panel" aria-hidden="true">
              <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "radial-gradient(35% 40% at 30% 28%, rgba(37,99,235,.45), transparent), radial-gradient(35% 40% at 72% 72%, rgba(22,163,74,.35), transparent)", filter: "blur(8px)" }} />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#7fa8ff]">Inside RecoverOS</div>
                <div className="mt-2 text-lg font-extrabold leading-snug" style={JK}>See exactly where your money is stuck</div>
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                  <div className="flex items-center justify-between text-[12px]">
                    <b style={JK}>Portfolio Intelligence</b>
                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#7ef0a9]"><span className="h-1.5 w-1.5 rounded-full bg-[#16A34A] ros-live-dot" />Live</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    <div className="rounded-lg bg-white/[0.06] px-3 py-2"><div className="text-base font-extrabold" style={JK}>₹4.82 Cr</div><div className="text-[10px] text-slate-400">Receivables</div></div>
                    <div className="rounded-lg bg-white/[0.06] px-3 py-2"><div className="text-base font-extrabold text-[#7ef0a9]" style={JK}>59.3%</div><div className="text-[10px] text-slate-400">Recovery</div></div>
                  </div>
                  <div className="mt-3 flex h-12 items-end gap-1.5">
                    {[40, 64, 50, 80, 66, 96].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${h}%`, background: i === 5 ? "#16A34A" : i % 2 ? "#2563eb" : "#0ea5a4" }} />
                    ))}
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-[12px] text-slate-300">
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#7ef0a9]" /> 30-min live walkthrough</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#7ef0a9]" /> Built on your own portfolio data</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
